import { observer } from 'mobx-react-lite';
import type { Dispatch, JSX, SetStateAction } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IGracePeriod, IProduct } from '@feathr/blackbox';
import { NumberInput, Select } from '@feathr/components';
import PackageDetailsCard from '@feathr/extender/App/Settings/GracePeriodSelect/PackageDetailsCard';
import { useAccount, useFlags } from '@feathr/extender/state';

import UpdatedLicenseDate from './UpdatedGracePeriodCard';

import * as styles from './GracePeriodSelect.css';

export interface IGracePeriodSelectProps {
  onChange: Dispatch<SetStateAction<IProduct | undefined>>;
  product?: IProduct;
}

function GracePeriodSelect({ onChange, product }: Readonly<IGracePeriodSelectProps>): JSX.Element {
  const account = useAccount();
  const { csmManager: hasAccountManagerFlag } = useFlags();
  const { t } = useTranslation();
  const { add_ons: addOns, discounts, packages, services } = account.get('license');

  const handleInputChange = (newValue: number, key: keyof IGracePeriod): void => {
    if (product) {
      onChange({
        ...product,
        grace_period: {
          ...product.grace_period,
          [key]: newValue,
        },
      });
    }
  };

  function handlePackageChange(newValue: IProduct): void {
    onChange(newValue);
  }

  function handleChangeStartDate(newValue?: number): void {
    handleInputChange(newValue ?? 0, 'start');
  }

  function handleChangeEndDate(newValue?: number): void {
    handleInputChange(newValue ?? 0, 'end');
  }

  function handleSelectOptionValue(pkg: IProduct): string {
    return pkg.name;
  }

  function handleSelectClear(): void {
    onChange(undefined);
  }

  function handleNoOptionsMessage(): string {
    return t('No license packages found on the account.');
  }

  const selectOptions = [...addOns, ...discounts, ...packages, ...services].filter(
    (item: IProduct) => item.opportunity_line_item_id,
  );

  return (
    <>
      <Select<IProduct>
        // Managing package grace period is reserved for Account Managers (previously known as CSM).
        disabled={!hasAccountManagerFlag}
        getOptionValue={handleSelectOptionValue}
        helpText={t(
          'Select a package on the account to add a grace period. Please contact support if the desired package is not listed.',
        )}
        isClearable={true}
        label={t('License package')}
        name={'grace-period-select'}
        noOptionsMessage={handleNoOptionsMessage}
        onChange={handlePackageChange}
        onClear={handleSelectClear}
        options={selectOptions}
        placeholder={t('Select package on the account...')}
        value={product}
        wrapperClassName={styles.select}
      />
      {product && (
        <PackageDetailsCard name={product.name} opp_id={product.opp_id} period={product.period} />
      )}

      <NumberInput
        clearableClassName={styles.numberInputWrapper}
        disabled={!hasAccountManagerFlag || !product}
        helpText={t('Add a grace period to the start of the license.')}
        label={t('Start earlier')}
        min={0}
        onChange={handleChangeStartDate}
        suffix={t('day', { count: product?.grace_period?.start ?? 0 })}
        value={product?.grace_period?.start}
      />
      {product && (
        <UpdatedLicenseDate
          gracePeriod={product.grace_period}
          gracePeriodKey={'start'}
          period={product.period}
        />
      )}

      <NumberInput
        clearableClassName={styles.numberInputWrapper}
        disabled={!hasAccountManagerFlag || !product}
        helpText={t('Add a grace period to the end of the license.')}
        label={t('End later')}
        min={0}
        onChange={handleChangeEndDate}
        suffix={t('day', { count: product?.grace_period?.end ?? 0 })}
        value={product?.grace_period?.end}
      />
      {product && (
        <UpdatedLicenseDate
          gracePeriod={product.grace_period}
          gracePeriodKey={'end'}
          period={product.period}
        />
      )}
    </>
  );
}

export default observer(GracePeriodSelect);
